import { AlertTriangle, CheckCircle2, Circle, User } from 'lucide-react'

import { cn } from 'lib/utils'

import { EmptyBlock } from '../EmptyBlock'
import { asArray, asNumber, asText } from '../payload'

/**
 * Renderers for the project elements (MYD-501) that have no location
 * counterpart. Payload shapes mirror backend/presentations/providers/
 * projectElements.js. The cover, details, photos and notes reuse the location
 * renderers, whose payload shapes they share.
 */

/** Progress over all tasks, with the three counts beneath. */
export function ProjectTaskSummaryBlock({ payload }: { payload: any }) {
	const total = asNumber(payload?.total) ?? 0
	const percent = asNumber(payload?.percentDone)
	if (total === 0) return <EmptyBlock>No tasks on this project yet.</EmptyBlock>

	const tiles: Array<[string, number, string]> = [
		['Open', asNumber(payload?.open) ?? 0, 'text-gray-900'],
		['Overdue', asNumber(payload?.overdue) ?? 0, 'text-red-600'],
		['Completed', asNumber(payload?.done) ?? 0, 'text-emerald-600'],
	]
	return (
		<div className="space-y-6">
			<div>
				<div className="mb-2 flex items-baseline justify-between">
					<span className="text-[18px] uppercase tracking-wide text-gray-500">Progress</span>
					<span className="text-[40px] font-bold leading-none text-primary">{percent === null ? '—' : `${percent}%`}</span>
				</div>
				<div className="h-4 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden>
					<div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, percent ?? 0))}%` }} />
				</div>
				<p className="mt-2 text-[16px] text-gray-500">
					{total} {total === 1 ? 'task' : 'tasks'}
					{(asNumber(payload?.closedLast30) ?? 0) > 0 && ` · ${asNumber(payload.closedLast30)} completed in the last 30 days`}
				</p>
			</div>
			<div className="grid grid-cols-3 gap-4">
				{tiles.map(([label, value, tone]) => (
					<div key={label} className="rounded-xl bg-gray-50 px-5 py-4">
						<div className="text-[16px] uppercase tracking-wide text-gray-500">{label}</div>
						<div className={cn('text-[40px] font-bold leading-tight', tone)}>{value}</div>
					</div>
				))}
			</div>
		</div>
	)
}

const LIST_TITLES: Record<string, string> = { open: 'Open', overdue: 'Overdue', recentlyClosed: 'Recently completed' }

export function ProjectTaskListBlock({ payload }: { payload: any }) {
	const tasks = asArray<any>(payload?.tasks)
	const status = asText(payload?.status) || 'open'
	const total = asNumber(payload?.total) ?? tasks.length
	if (tasks.length === 0) return <EmptyBlock>No {(LIST_TITLES[status] ?? 'open').toLowerCase()} tasks.</EmptyBlock>

	const closed = status === 'recentlyClosed'
	return (
		<div>
			<ul className="divide-y divide-gray-100">
				{tasks.map((task, index) => {
					// An "open" list leads with its overdue tasks; each row says which it is.
					const overdue = !closed && task?.overdue === true
					const Icon = closed ? CheckCircle2 : overdue ? AlertTriangle : Circle
					return (
						<li key={index} className="flex items-center gap-4 py-3">
							<Icon className={cn('h-6 w-6 shrink-0', closed ? 'text-emerald-500' : overdue ? 'text-red-500' : 'text-gray-400')} />
							<div className="min-w-0 flex-1">
								<p className="truncate text-[21px] font-medium text-gray-900">{asText(task?.name)}</p>
								<p className="text-[16px] text-gray-500">
									{[asText(task?.priority), asText(task?.assignee)].filter(Boolean).join(' · ') || 'Unassigned'}
								</p>
							</div>
							<span className={cn('shrink-0 text-[17px] tabular-nums', overdue ? 'font-semibold text-red-600' : 'text-gray-600')}>
								{closed ? asText(task?.completed) : asText(task?.due) && `Due ${asText(task.due)}`}
							</span>
						</li>
					)
				})}
			</ul>
			{total > tasks.length && <p className="mt-3 text-[16px] text-gray-500">and {total - tasks.length} more</p>}
		</div>
	)
}

export function ProjectPartnersBlock({ payload }: { payload: any }) {
	const partners = asArray<any>(payload?.partners)
	const more = asNumber(payload?.more) ?? 0
	const owner = asText(payload?.owner)
	if (partners.length === 0 && !owner) return <EmptyBlock>No partners on this project yet.</EmptyBlock>

	const people: Array<{ name: string; company: string; role?: string }> = [
		...(owner ? [{ name: owner, company: '', role: 'Owner' }] : []),
		...partners.map(partner => ({ name: asText(partner?.name), company: asText(partner?.company) })),
	]
	return (
		<div>
			<ul className="grid grid-cols-2 gap-3">
				{people.map((person, index) => (
					<li key={index} className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3">
						<span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
							<User className="h-6 w-6" />
						</span>
						<div className="min-w-0">
							<p className="truncate text-[20px] font-medium text-gray-900">{person.name}</p>
							<p className="truncate text-[15px] text-gray-500">{person.role ?? (person.company || 'Partner')}</p>
						</div>
					</li>
				))}
			</ul>
			{more > 0 && <p className="mt-3 text-[16px] text-gray-500">and {more} more</p>}
		</div>
	)
}
